/**
 * Brings open tabs onto a new deploy (lib/appVersion).
 *
 * Checks every 5 minutes and whenever the tab comes back into view. Once a
 * newer build is out:
 *   - the next move to another page loads it, as a full page load instead of a
 *     client-side route change, so leaving a page is when the new app arrives;
 *   - a banner offers to reload now;
 *   - the /tv kiosk reloads straight away, since nobody is there to click.
 *
 * Only the path counts as a move: tabs and filters that live in the query
 * string do not reload the page under the reader.
 */

import { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { RefreshCw } from 'lucide-react'
import { Button } from './ui'
import { CURRENT_BUILD, fetchDeployedBuild, isOutdated } from '../lib/appVersion'

const CHECK_MS = 5 * 60 * 1000

export default function NewVersionNotice() {
  const { pathname } = useLocation()
  const [outdated, setOutdated] = useState(false)
  const loadedPath = useRef(pathname)

  useEffect(() => {
    if (!CURRENT_BUILD) return undefined
    let cancelled = false
    const check = async () => {
      if (document.visibilityState === 'hidden') return
      const deployed = await fetchDeployedBuild()
      if (!cancelled && isOutdated(CURRENT_BUILD, deployed)) setOutdated(true)
    }
    const onVisible = () => { if (document.visibilityState === 'visible') check() }
    const timer = setInterval(check, CHECK_MS)
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      cancelled = true
      clearInterval(timer)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [])

  useEffect(() => {
    if (!outdated) return
    if (pathname.startsWith('/tv') || pathname !== loadedPath.current) window.location.reload()
  }, [outdated, pathname])

  // Until a new build is out, keep the path the tab is on, so the first move
  // after one is noticed reloads, and not the move that came before it.
  useEffect(() => {
    if (!outdated) loadedPath.current = pathname
  }, [outdated, pathname])

  if (!outdated) return null
  return (
    <div role="status" className="fixed bottom-4 right-4 z-50 flex items-center gap-3 rounded-md border bg-background px-4 py-3 text-sm shadow-lg">
      <span>A new version is available.</span>
      <Button size="sm" onClick={() => window.location.reload()}>
        <RefreshCw className="mr-1.5 h-3.5 w-3.5" /> Reload
      </Button>
    </div>
  )
}
